import PropTypes from "prop-types";
import React from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { SelectFormField, TextFormField, useWatch } from "@swa-ui/form";
import i18n from "@swa-ui/locale";

import {
  MAX_ANSWER_LENGTH,
  SECURITY_QUESTIONS_FORM_FIELD_SCHEMA_FIELD_NAMES,
} from "./SecurityQuestionsFormField.schema";

/**
 * SecurityQuestionsFormField renders a form field with two security questions and answers.
 */
export const SecurityQuestionsFormField = (props) => {
  const {
    answer1ClassName,
    answer2ClassName,
    defaultQuestion1,
    defaultQuestion2,
    disabled,
    question1ClassName,
    question2ClassName,
  } = props;
  const question1Value = useWatch({ name: "question1" });
  const question2Value = useWatch({ name: "question2" });
  const { securityQuestionOptionsv2 } = getBootstrapData("login-update-customer-information-data");
  const defaultQuestionValue = "Select";
  const questions = [
    {
      content: i18n("SecurityQuestionsFormField__SELECT"),
      value: defaultQuestionValue,
    },
    ...securityQuestionOptionsv2,
  ].map((item) => ({
    label: i18n(item?.content),
    value: item?.value,
  }));

  return (
    <>
      <SelectFormField {...getSecurityQuestion1Props()} />
      <TextFormField {...getSecurityAnswer1Props()} />
      <SelectFormField {...getSecurityQuestion2Props()} />
      <TextFormField {...getSecurityAnswer2Props()} />
    </>
  );

  function getSecurityQuestion1Props() {
    const question1List = questions.filter(
      (question) => question.value === defaultQuestionValue || question.value !== question2Value
    );

    return {
      className: question1ClassName,
      componentProps: {
        defaultValue: defaultQuestion1 || defaultQuestionValue,
        list: question1List,
        required: true,
      },
      disabled,
      label: i18n("SecurityQuestionsFormField__SECURITY_QUESTION_1"),
      name: SECURITY_QUESTIONS_FORM_FIELD_SCHEMA_FIELD_NAMES.QUESTION1,
    };
  }

  function getSecurityAnswer1Props() {
    return {
      className: answer1ClassName,
      componentProps: {
        maxLength: MAX_ANSWER_LENGTH,
        required: true,
      },
      disabled,
      label: i18n("SecurityQuestionsFormField__SECURITY_ANSWER_1"),
      name: SECURITY_QUESTIONS_FORM_FIELD_SCHEMA_FIELD_NAMES.ANSWER1,
    };
  }

  function getSecurityQuestion2Props() {
    const question2List = questions.filter(
      (question) => question.value === defaultQuestionValue || question.value !== question1Value
    );

    return {
      className: question2ClassName,
      componentProps: {
        defaultValue: defaultQuestion2 || defaultQuestionValue,
        list: question2List,
        required: true,
      },
      disabled,
      label: i18n("SecurityQuestionsFormField__SECURITY_QUESTION_2"),
      name: SECURITY_QUESTIONS_FORM_FIELD_SCHEMA_FIELD_NAMES.QUESTION2,
    };
  }

  function getSecurityAnswer2Props() {
    return {
      className: answer2ClassName,
      componentProps: {
        maxLength: MAX_ANSWER_LENGTH,
        required: true,
      },
      disabled,
      label: i18n("SecurityQuestionsFormField__SECURITY_ANSWER_2"),
      name: SECURITY_QUESTIONS_FORM_FIELD_SCHEMA_FIELD_NAMES.ANSWER2,
    };
  }
};

SecurityQuestionsFormField.propTypes = {
  //* className for answer1 field */
  answer1ClassName: PropTypes.string,

  //* className for answer2 field */
  answer2ClassName: PropTypes.string,

  //* disabled state for all fields */
  disabled: PropTypes.bool,

  //* className for question1 field */
  question1ClassName: PropTypes.string,

  //* className for question2 field */
  question2ClassName: PropTypes.string,

  //* array of security questions and answers */
  securityQuestions: PropTypes.arrayOf(PropTypes.string),
};
